import { ref } from 'vue';
import { Loader } from '@googlemaps/js-api-loader';
import { mapsConfig } from '@/config';
//! Don't try to shorten this import statement.
//! If you do, a usage of the vendor property from the form defaults constants breaks.
//! I have absolutely no idea why this happens...
import { googleMapsDefaults } from '@/constants/google-maps-defaults';
import { exponentialBackOff } from '@/utils/exponential-back-off';
import { Route } from '@/types';
import { Vendor } from '@/types/app';

export const googleMapsApiOnLoad = () => new Loader(mapsConfig).load();

const map = ref<google.maps.Map>();

const createMap = (element: HTMLElement, latLng: google.maps.LatLngLiteral = googleMapsDefaults.mapCenter) => {
  map.value = new google.maps.Map(element, {
    center: latLng,
    styles: googleMapsDefaults.styles,
    zoom: googleMapsDefaults.zoom
  });

  return map;
};

const useMap = (): [typeof map, typeof createMap] => [map, createMap];

const latLng = ref<google.maps.LatLng>();
const latLngListener = ref<google.maps.MapsEventListener>();

const createLatLngOnClickListener = () => {
  latLngListener.value = map.value?.addListener('contextmenu', (event: google.maps.MapMouseEvent) => {
    //! This is fragile code.
    //! It is not guaranteed that a mouse is being used.
    if (event.latLng) {
      latLng.value = event.latLng;
    }
  });
};

const useLatLngOnClick = (): [typeof latLng, typeof createLatLngOnClickListener] => {
  return [latLng, createLatLngOnClickListener];
};

const useAutocomplete = (element: HTMLInputElement) => {
  return new google.maps.places.Autocomplete(element, {
    types: ['geocode'],
    componentRestrictions: {
      country: 'USA'
    },
    fields: ['formatted_address', 'geometry']
  });
};

const originLatLng = ref<google.maps.LatLng>();
const originMarker = ref<google.maps.Marker>();
const removeOriginMarker = () => {
  originMarker.value?.setMap(null);
  //* For some reason setting marker to undefined does not remove it from the map.
  //* Users should only put a marker on the map a handful of times.
  //* Therefore, there shouldn't be any performance concerns.
  originMarker.value?.setVisible(false);
};
const setOriginMarker = (location: google.maps.LatLng, setCenter = false) => {
  originLatLng.value = location;

  if (!map.value) return;

  removeOriginMarker();

  const icon: google.maps.Symbol = {
    ...googleMapsDefaults.icon,
    //* Cannot use the google namespace in a constants file. Due to it not being immediately loaded.
    //* The default icon used is 24dp and the following Point is hardcoded to use that size for relative zoom positioning.
    anchor: new google.maps.Point(12, 24),
    fillColor: googleMapsDefaults.originMarkerColor,
    path: googleMapsDefaults.originMarkerIcon
  };

  originMarker.value = new google.maps.Marker({
    position: location,
    map: map.value,
    title: 'Origin',
    icon
  });

  if (setCenter) {
    map.value.setCenter(location);
  }
};

const useOriginMarker = () => {
  return {
    originLatLng,
    originMarker,
    setOriginMarker,
    removeOriginMarker
  };
};

const markers = ref<Map<number, google.maps.Marker>>(new Map());
const useMarkers = () => {
  const createMarker = (
    markerOptions: Partial<google.maps.MarkerOptions>,
    iconOptions?: Partial<google.maps.Symbol>
  ): [google.maps.Marker, number] => {
    const icon: google.maps.Symbol = {
      ...googleMapsDefaults.icon,
      //* Cannot use the google namespace in a constants file. Due to it not being immediately loaded.
      //* The default icon used is 24dp and the following Point is hardcoded to use that size for relative zoom positioning.
      anchor: new google.maps.Point(12, 24),
      ...iconOptions
    };

    const marker = new google.maps.Marker({
      map: map.value,
      icon,
      ...markerOptions
    });

    const key = markers.value.size + 1;
    markers.value.set(key, marker);
    return [marker, key];
  };

  const removeMarker = (key: number) => {
    const marker = markers.value.get(key);
    marker?.setMap(null);
    marker?.setVisible(false);
    markers.value.delete(key);
  };

  const removeAllMarkers = () => {
    markers.value.forEach((marker, key, markersMap) => {
      marker.setMap(null);
      marker.setVisible(false);
      markersMap.delete(key);
    });
  };

  return {
    markers,
    createMarker,
    removeMarker,
    removeAllMarkers
  };
};

const renderers = ref<Map<number, google.maps.DirectionsRenderer>>(new Map());
const useDirections = () => {
  const directionsService = new google.maps.DirectionsService();
  const createDirectionsRenderer = (mapRef: google.maps.Map): [google.maps.DirectionsRenderer, number] => {
    const directionsRenderer = new google.maps.DirectionsRenderer({
      draggable: true,
      map: mapRef
    });

    renderers.value.set(renderers.value.size + 1, directionsRenderer);

    return [directionsRenderer, renderers.value.size + 1];
  };

  const removeRenderer = (key: number) => {
    const renderer = renderers.value.get(key);
    renderer?.setMap(null);
    renderers.value.delete(key);
  };

  const removeAllRenderers = () => {
    renderers.value.forEach((renderer, key, renderersMap) => {
      renderer.setMap(null);
      renderersMap.delete(key);
    });
  };

  const getDirections = (directionsRequest: google.maps.DirectionsRequest, vendor: Vendor, color: string) =>
    exponentialBackOff(
      () =>
        new Promise<Route>((resolve, reject) =>
          directionsService.route(directionsRequest, (response, status) => {
            status === google.maps.DirectionsStatus.OK
              ? resolve({
                  directionsResult: response,
                  vendor,
                  color
                })
              : reject(new Error(status));
          })
        )
    );

  return {
    directionsService,
    renderers,
    createDirectionsRenderer,
    removeAllRenderers,
    removeRenderer,
    getDirections
  };
};

const getLinearDistance = (from: google.maps.LatLng, to: google.maps.LatLng, radius?: number | undefined) =>
  google.maps.geometry.spherical.computeDistanceBetween(from, to, radius);

const useGeometry = () => ({
  getLinearDistance
});

const useGeocoding = () => {
  const geocoder = new google.maps.Geocoder();

  const getAddress = (location: google.maps.LatLng): Promise<string | undefined> =>
    new Promise((resolve, reject) =>
      geocoder.geocode({ location }, (result, status) => {
        status === google.maps.GeocoderStatus.OK
          ? resolve(result?.[0].formatted_address)
          : reject(new Error(`Error getting address, returned with status: ${status}`));
      })
    );

  return {
    getAddress
  };
};

export const useGoogleMaps = () => ({
  useMap,
  useLatLngOnClick,
  useAutocomplete,
  useOriginMarker,
  useDirections,
  useMarkers,
  useGeometry,
  useGeocoding
});
